/**
 * PluginManager handles plugin registration, initialization, and lifecycle
 */
class PluginManager {
  constructor(editor) {
    this.editor = editor;
    this.plugins = new Map();
    this.pluginInstances = new Map();
  }

  /**
   * Register a plugin class
   * @param {string} name - Plugin name/identifier
   * @param {Class} PluginClass - Plugin class constructor
   */
  register(name, PluginClass) {
    if (this.plugins.has(name)) {
      console.warn(`Plugin "${name}" is already registered`);
      return;
    }
    this.plugins.set(name, PluginClass);
  }

  /**
   * Load and initialize a plugin
   * @param {string} name - Plugin name
   * @param {Object} options - Plugin options
   * @returns {Plugin} Plugin instance
   */
  load(name, options = {}) {
    if (this.pluginInstances.has(name)) {
      return this.pluginInstances.get(name);
    }

    const PluginClass = this.plugins.get(name);
    if (!PluginClass) {
      throw new Error(`Plugin "${name}" is not registered`);
    }

    const instance = new PluginClass(this.editor, options);
    instance.init();
    this.pluginInstances.set(name, instance);
    return instance;
  }

  /**
   * Unload a plugin
   * @param {string} name - Plugin name
   */
  unload(name) {
    const instance = this.pluginInstances.get(name);
    if (instance) {
      instance.destroy();
      this.pluginInstances.delete(name);
    }
  }

  /**
   * Get a loaded plugin instance
   * @param {string} name - Plugin name
   * @returns {Plugin|undefined}
   */
  get(name) {
    return this.pluginInstances.get(name);
  }

  /**
   * Check if a plugin is loaded
   * @param {string} name - Plugin name
   * @returns {boolean}
   */
  isLoaded(name) {
    return this.pluginInstances.has(name);
  }

  /**
   * Get all loaded plugin instances
   * @returns {Array} Array of plugin instances
   */
  getAllInstances() {
    return Array.from(this.pluginInstances.values());
  }

  /**
   * Get all registered plugin names
   * @returns {Array} Array of plugin names
   */
  getRegisteredPlugins() {
    return Array.from(this.plugins.keys());
  }

  /**
   * Execute a command across all loaded plugins
   * @param {string} command - Command name
   * @param {*} value - Optional command value
   * @returns {boolean} true if any plugin handled the command
   */
  executeCommand(command, value) {
    for (const instance of this.pluginInstances.values()) {
      if (instance.enabled) {
        try {
          instance.executeCommand(command, value);
        } catch (error) {
          console.error('Error executing command in plugin:', error);
        }
      }
    }
  }

  /**
   * Handle keyboard events across all loaded plugins
   * @param {KeyboardEvent} event
   * @returns {boolean} true if any plugin handled the event
   */
  handleKeyboard(event) {
    for (const instance of this.pluginInstances.values()) {
      if (instance.enabled && instance.handleKeyboard(event)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Get toolbar buttons from all loaded plugins
   * @returns {Array} Array of toolbar button groups
   */
  getToolbarButtons() {
    const groups = [];
    for (const instance of this.pluginInstances.values()) {
      if (instance.enabled) {
        const buttons = instance.getToolbarButtons();
        if (buttons && buttons.length > 0) {
          groups.push(...buttons);
        }
      }
    }
    return groups;
  }

  /**
   * Destroy all plugins
   */
  destroyAll() {
    for (const instance of this.pluginInstances.values()) {
      instance.destroy();
    }
    this.pluginInstances.clear();
  }
}

export default PluginManager;
